<template>
	<AccessibleSection
		name="announcementbanner"
		class="announcement-banner"
		:tabOrder="tabOrder"
	>
		<NotificationPrompt
			v-if="store.askForNotificationPermission"
			class="banner-item"
			@close="dismissPrompt"
		/>
		<div
			class="banner-item"
			v-for="(announcement, index) in unreadAnnouncements"
			:key="announcement.id"
			@click="showDetail(announcement)"
			:class="{ dismissing: dismissedIds.includes(announcement.id) }"
		>
			<!-- Render all unread announcements, but only render content of the top two -->
			<div class="banner-item-container" v-if="index < 2">
				<div class="banner-item-header">
					<AnnouncementSender
						:announcement="announcement"
						showDate
						profileSize="13px"
					/>
					<CloseIcon
						class="dismiss-btn"
						@click.stop="dismiss(announcement.id)"
					/>
				</div>
				<div class="banner-item-content">
					<PhotoVideoThumbnail
						class="banner-item-thumb"
						v-if="
							announcement.hasMedia &&
							(announcement.media.isPhoto || announcement.media.isVideo)
						"
						:media="announcement.media"
						isSent
					/>
					<div
						class="banner-item-text"
						data-private
						v-text="announcement.text"
					/>
				</div>
			</div>
		</div>
	</AccessibleSection>
</template>
<script>
import store, { getUnreadAnnouncementsWithSenders } from '@/store'
import { PhotoVideoThumbnail } from '@/components/common'
import { AccessibleSection } from '@/components/common/Accessible'
import NotificationPrompt from '../NotificationPrompt'
import AnnouncementSender from './AnnouncementSender'
import { CloseIcon } from '@/icons'
import { markAnnouncementAsRead } from '@/api/announcement'

export default {
	name: 'AnnouncementBanner',
	components: {
		AnnouncementSender,
		CloseIcon,
		NotificationPrompt,
		PhotoVideoThumbnail,
		AccessibleSection,
	},

	data() {
		return {
			store,
			dismissedIds: [],
			unreadAnnouncements: getUnreadAnnouncementsWithSenders(),
		}
	},

	created() {
		this.$onEventBus('announcement-update', () => {
			this.unreadAnnouncements = getUnreadAnnouncementsWithSenders()
		})
	},

	methods: {
		dismissPrompt() {
			store.askForNotificationPermission = false
			sessionStorage.setItem('hideNotificationPrompt', true)
		},

		dismiss(id) {
			this.dismissedIds.push(id)
			markAnnouncementAsRead(id)
			// manually remove id from the array for animation
			setTimeout(() => this.unreadAnnouncements.shift(), 100)
		},

		showDetail(announcement) {
			this.$emit('detail', announcement)
		},
	},

	computed: {
		tabOrder() {
			if (store.askForNotificationPermission) {
				return [
					{ css: '.enable-text', clickOnEnter: true },
					{ css: '.notification-link:first-child', clickOnEnter: true },
					{ css: '.notification-link:nth-child(3)', clickOnEnter: true },
				]
			}
			return [{ css: '.banner-item:first-child', clickOnEnter: true }]
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

@media screen and (max-width: 800px) {
	.banner-item {
		left: 8px !important;
		right: 8px !important;
		width: auto !important;
	}
}

.announcement-banner {
	background: var(--sidebar-gray);
	border-bottom: 1px solid var(--neutral-40040);
	height: $announcement-banner-height;

	.banner-item {
		width: 100%;
		max-width: 800px;
		position: absolute;
		left: calc(50% - 400px);
		margin: 25px auto 0 auto;
		border-radius: 6px;
		height: 90px;
		opacity: 1;
		padding: 10px 16px;
		transition-delay: 800ms;
		transition: transform 100ms;
		display: none;
		cursor: pointer;

		&:nth-child(1) {
			z-index: 3;
			background-color: var(--blue-300);
			display: block;
		}

		&:nth-child(2) {
			z-index: 2;
			transform: translateY(-14px) scaleY(0.9) scaleX(0.95);
			background-color: color-mix(in srgb, var(--blue-300), white 40%);
			display: block;
		}

		&:nth-child(3) {
			z-index: 1;
			transform: translateY(-26px) scaleY(0.8) scaleX(0.9);
			background-color: color-mix(in srgb, var(--blue-300), white 60%);
			display: block;
		}

		&.dismissing {
			animation: dismiss-announcement 300ms;
		}

		.banner-item-header {
			@include flex(8px);
			width: 100%;
			margin-bottom: 8px;

			:deep(.announcement-sender) {
				color: white;
				width: 100%;
				height: unset;
				font-size: 11px;
				.message-time {
					color: white;
					font-size: 11px;
				}
				.translation-globe {
					color: white;
				}
			}

			.dismiss-btn {
				cursor: pointer;
				color: white;
				font-size: 18px;
				&:hover {
					color: var(--blue-100);
				}
			}
		}
		.banner-item-content {
			@include flex(12px);
			align-items: flex-start;

			.banner-item-thumb {
				font-size: 38px;
				flex-shrink: 0;
				overflow: hidden;
				border-radius: 4px;
			}
			.banner-item-text {
				color: white;
				@include clamp(2);
				font-size: 14px;
				flex-grow: 1;
			}
		}
	}
}

@keyframes dismiss-announcement {
	from {
		opacity: 1;
	}
	to {
		opacity: 0.6;
		transform: translateY(-150px);
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';
@include overrideScheme('dark') {
	.announcement-banner {
		background: var(--neutral-100);

		.banner-item {
			&:nth-child(2) {
				background-color: #367093;
			}
			&:nth-child(3) {
				background-color: #0a3c5a;
			}
		}
	}
}
</style>
